import { ChangeDetectionStrategy, Component, inject, input, signal } from '@angular/core';

import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { SessionsService } from '@generated/services/sessions.service';
import { NotificationService } from '@core/services/notification.service';

/**
 * Fetches an observer LiveKit token for an active session and opens the
 * agent-test route in a new tab with ?roomToken=<jwt>. agent-test sees
 * the param, skips dispatch and joins the existing room read-only.
 */
@Component({
  selector: 'app-live-listen-button',
  standalone: true,
  imports: [ButtonModule, TooltipModule],
  templateUrl: './live-listen-button.component.html',
  styleUrl: './live-listen-button.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LiveListenButtonComponent {
  private sessionsService = inject(SessionsService);
  private notificationService = inject(NotificationService);
  private router = inject(Router);

  readonly agentId = input<string | null>(null);
  readonly sessionId = input<string | null>(null);
  readonly label = input<string>($localize`:@@liveListen.label:Live`);
  readonly size = input<'small' | 'large' | undefined>(undefined);
  readonly outlined = input<boolean>(false);

  readonly isLoading = signal<boolean>(false);

  open(): void {
    const agentId = this.agentId();
    const sessionId = this.sessionId();
    if (!agentId || !sessionId || this.isLoading()) return;

    this.isLoading.set(true);
    this.sessionsService.apiSessionsSessionIdObserverTokenGet({ sessionId }).subscribe({
      next: (result) => {
        this.isLoading.set(false);
        if (!result.room_token) {
          this.notificationService.showError(
            $localize`:@@liveListen.notify.tokenFail:Failed to get observer token`
          );
          return;
        }
        this.router.navigate(['/agents', agentId, 'test'], {
          queryParams: { roomToken: result.room_token }
        });
      },
      error: (error: { status?: number }) => {
        this.isLoading.set(false);
        const status = error?.status;
        const msg = status === 409 || status === 410
          ? $localize`:@@liveListen.notify.sessionInactive:Session is no longer active`
          : $localize`:@@liveListen.notify.tokenFail:Failed to get observer token`;
        this.notificationService.showError(msg);
      }
    });
  }
}
